import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { pbEngineer } from '../lib/pb';
import { useData } from '../data/DataProvider';
import EmptyState from '../shared/components/EmptyState';
import Skeleton from '../shared/components/Skeleton';
import { Icons } from '../shared/icons';
import SafeIcon from '../common/SafeIcon';

export default function EngineerMapping() {
  const { data } = useData();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [etaMinutes, setEtaMinutes] = useState(30);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    loadJobs();
  }, []);

  const loadJobs = async () => {
    try {
      const engineerId = pbEngineer.authStore.record?.id;
      if (!engineerId) return;

      const result = await pbEngineer.collection('engineer_jobs').getList(1, 20, {
        filter: `engineer_id="${engineerId}" && (status="assigned" || status="en_route")`,
        sort: 'assigned_at',
        expand: 'client_id',
      });

      setJobs(result.items);
      if (result.items.length > 0) {
        setSelectedJob(result.items[0]);
      }
    } catch (err) {
      console.error('Failed to load jobs:', err);
    } finally {
      setLoading(false);
    }
  };

  const sendEtaUpdate = async (status) => {
    if (!selectedJob) return;
    
    setSending(true);
    try {
      const location = await getCurrentLocation();

      await pbEngineer.collection('eta_updates').create({
        tenant_id: data.tenant.id,
        client_id: selectedJob.client_id,
        engineer_id: pbEngineer.authStore.record.id,
        engineer_job_id: selectedJob.id,
        status: status,
        eta_minutes: etaMinutes,
        message: getMessageForStatus(status),
        current_location: location,
        notification_sent: false,
        created_at: new Date().toISOString(),
      });

      // Location write (spec §2.6): "I'm en route" posts a live location
      // record — feeds the admin map + future ETA math. Degrades silently
      // when geolocation is unavailable.
      if (status === 'on_way' && location) {
        await pbEngineer.collection('engineer_locations').create({
          tenant_id: data.tenant.id,
          engineer_id: pbEngineer.authStore.record.id,
          engineer_job_id: selectedJob.id,
          coordinates: { lat: location.lat, lng: location.lng },
          accuracy: String(location.accuracy || ''),
          timestamp: new Date().toISOString(),
        }).catch(() => {});
      }

      // Update job status
      await pbEngineer.collection('engineer_jobs').update(selectedJob.id, {
        status: status === 'on_way' ? 'en_route' : status === 'arrived' ? 'on_site' : selectedJob.status,
      });

      alert(`ETA update sent to client: ${getMessageForStatus(status)}`);
      
      // Reload jobs
      await loadJobs();
      
    } catch (err) {
      console.error('Failed to send ETA update:', err);
      alert('Failed to send update. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const getMessageForStatus = (status) => {
    const messages = {
      on_way: `Engineer on the way! ETA: ${etaMinutes} minutes`,
      delayed: `Running late. New ETA: ${etaMinutes} minutes`,
      arrived: `Engineer has arrived at your location`,
      completed: `Work has been completed`
    };
    return messages[status] || 'Status update';
  };

  const getCurrentLocation = async () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy,
          });
        },
        (error) => {
          console.error('Geolocation error:', error);
          resolve(null);
        }
      );
    });
  };

  const openMapping = (job) => {
    if (job.coordinates && typeof job.coordinates === 'object') {
      const { lat, lng } = job.coordinates;
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, '_blank');
    } else if (job.address) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(job.address)}`, '_blank');
    } else {
      alert('No location information available for this job');
    }
  };

  if (loading) {
    return <div className="page-container"><Skeleton rows={6} /></div>;
  }

  return (
    <div className="page-container mapping-page">
      <div className="page-header">
        <h1>Job Mapping & ETA</h1>
        <p>Manage your routes and keep clients informed</p>
      </div>

      <div className="mapping-layout">
        <div className="jobs-sidebar">
          <h3>Assigned Jobs</h3>
          {jobs.length === 0 ? (
            <EmptyState
              title="No active jobs"
              message="You have no jobs that require navigation"
              icon={Icons.Map}
            />
          ) : (
            <div className="jobs-list">
              {jobs.map(job => (
                <div
                  key={job.id}
                  className={`job-item ${selectedJob?.id === job.id ? 'active' : ''} ${job.priority === 'urgent' ? 'urgent' : ''}`}
                  onClick={() => setSelectedJob(job)}
                >
                  <div className="job-item-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span className={`job-status status-${job.status === 'assigned' ? 'blue' : 'yellow'}`}>
                        {job.status}
                      </span>
                      {job.priority === 'urgent' && (
                        <span className="priority-chip urgent">Urgent</span>
                      )}
                    </div>
                    <span className="job-time">
                      {new Date(job.assigned_at || job.created).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <h4>{job.title}</h4>
                  <p className="job-client">{job.expand?.client_id?.name || 'Unknown Client'}</p>
                  <p className="job-address">{job.address}</p>
                  {job.estimated_duration_minutes && (
                    <div className="job-duration">
                      <SafeIcon icon={Icons.Clock} />
                      Est. {job.estimated_duration_minutes} min
                    </div>
                  )}
                  <button className="secondary-button small" onClick={() => openMapping(job)}>
                    <SafeIcon icon={Icons.Navigation} />
                    Navigate
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mapping-main">
          {selectedJob ? (
            <>
              <div className="selected-job-card">
                <div className="job-card-header">
                  <h2>{selectedJob.title}</h2>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {selectedJob.priority === 'urgent' && (
                      <span className="priority-chip urgent">
                        <SafeIcon icon={Icons.AlertTriangle} size={11} /> Urgent
                      </span>
                    )}
                    <span className={`job-status status-${selectedJob.status === 'assigned' ? 'blue' : 'yellow'}`}>
                      {selectedJob.status}
                    </span>
                  </div>
                </div>
                
                <div className="job-details">
                  <div className="detail-row">
                    <SafeIcon icon={Icons.User} />
                    <div>
                      <span className="label">Client</span>
                      <span className="value">{selectedJob.expand?.client_id?.name || 'Unknown'}</span>
                    </div>
                  </div>
                  
                  <div className="detail-row">
                    <SafeIcon icon={Icons.MapPin} />
                    <div>
                      <span className="label">Address</span>
                      <span className="value">{selectedJob.address}</span>
                    </div>
                  </div>

                  {selectedJob.estimated_duration_minutes && (
                    <div className="detail-row">
                      <SafeIcon icon={Icons.Clock} />
                      <div>
                        <span className="label">Estimated Duration</span>
                        <span className="value">{selectedJob.estimated_duration_minutes} minutes</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mapping-links">
                  <h3>Quick Links</h3>
                  <div className="links-grid">
                    <button className="link-button" onClick={() => openMapping(selectedJob)}>
                      <SafeIcon icon={Icons.Navigation} />
                      <span>Google Maps</span>
                    </button>
                    <button className="link-button" onClick={() => window.open(`https://waze.com/ul?q=${encodeURIComponent(selectedJob.address)}`, '_blank')}>
                      <SafeIcon icon={Icons.Map} />
                      <span>Waze</span>
                    </button>
                    <button className="link-button" onClick={() => window.open(`https://www.apple.com/maps/?dirflg=d&q=${encodeURIComponent(selectedJob.address)}`, '_blank')}>
                      <SafeIcon icon={Icons.Compass} />
                      <span>Apple Maps</span>
                    </button>
                  </div>
                </div>
              </div>

              <div className="eta-controls">
                <h3>Send Client Update</h3>
                
                <div className="eta-input-group">
                  <label>Estimated Arrival Time</label>
                  <div className="eta-slider">
                    <input
                      type="range"
                      min="5"
                      max="120"
                      step="5"
                      value={etaMinutes}
                      onChange={(e) => setEtaMinutes(parseInt(e.target.value))}
                    />
                    <div className="eta-display">
                      <strong>{etaMinutes}</strong> minutes
                    </div>
                  </div>
                </div>

                <div className="eta-actions">
                  <button 
                    className="primary-button"
                    onClick={() => sendEtaUpdate('on_way')}
                    disabled={sending}
                  >
                    <SafeIcon icon={Icons.Navigation} />
                    On My Way
                  </button>
                  
                  <button 
                    className="warning-button"
                    onClick={() => sendEtaUpdate('delayed')}
                    disabled={sending}
                  >
                    <SafeIcon icon={Icons.Clock} />
                    Running Late
                  </button>
                  
                  <button 
                    className="success-button"
                    onClick={() => sendEtaUpdate('arrived')}
                    disabled={sending}
                  >
                    <SafeIcon icon={Icons.CheckCircle} />
                    Arrived
                  </button>
                  
                  <button 
                    className="secondary-button"
                    onClick={() => sendEtaUpdate('completed')}
                    disabled={sending}
                  >
                    <SafeIcon icon={Icons.CheckCircle} />
                    Work Complete
                  </button>
                </div>

                <div className="eta-preview">
                  <label>Message Preview</label>
                  <p>{getMessageForStatus('on_way')}</p>
                </div>
              </div>
            </>
          ) : (
            <EmptyState
              title="Select a job"
              message="Choose a job from the list to see mapping options and send updates"
              icon={Icons.Map}
            />
          )}
        </div>
      </div>
    </div>
  );
}